"use client";

/**
 * "Ver en español" — the suggestion a default-locale public page offers a
 * visitor whose browser prefers another supported language.
 *
 * A suggestion and never a redirect: redirecting on `Accept-Language` strands
 * the bilingual, the traveller and every crawler (which sends none), and these
 * pages are static files that cannot vary by header anyway. So it is decided
 * in the browser from `navigator.languages`, after hydration.
 *
 * It stays quiet once the visitor has chosen anything — the locale cookie
 * exists — and dismissing it is a choice: the cookie is written with the page's
 * own locale, so it does not come back.
 *
 * The copy arrives as props, one entry per suggestible locale, read by the
 * server from *that* locale's catalog: the offer to read Spanish is made in
 * Spanish.
 */
import { DEFAULT_LOCALE, type Locale } from "@reflect/domain";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { localisedPath, type PublicPath } from "../../lib/i18n/alternates";
import { readLocaleCookie, writeLocaleCookie } from "../../lib/i18n/localeCookie";
import { CloseIcon } from "../account/icons";

/** One suggestible locale's banner copy, in that locale. */
export interface LocaleSuggestionCopy {
  /** "Esta página está disponible en español." */
  suggestion: string;
  /** The link's label. */
  suggestionAction: string;
  /** The dismiss button's accessible label. */
  suggestionDismiss: string;
}

/**
 * Exported for tests.
 *
 * The first of the browser's preferred languages that the product supports, if
 * it is one of `offered` — or `null` when the first supported preference is the
 * page's own language (a visitor who ranks English above Spanish is not asked).
 *
 * Matches on the primary subtag: `es-MX` and `es-419` both want `es`.
 *
 * @param preferred - `navigator.languages`, most preferred first.
 * @param offered - the locales a suggestion exists for.
 */
export function suggestedLocale(
  preferred: readonly string[],
  offered: readonly Locale[],
): Locale | null {
  for (const tag of preferred) {
    const primary = tag.split("-")[0]?.toLowerCase();
    if (primary === DEFAULT_LOCALE) return null;
    const match = offered.find((locale) => locale === primary);
    if (match) return match;
  }
  return null;
}

/**
 * Whether `path` has the one shape `PublicPath` requires: a leading slash.
 *
 * @param path - the router's pathname.
 */
function isPublicPath(path: string): path is PublicPath {
  return path.startsWith("/");
}

/** Props for {@link LocaleSuggestion}. */
export interface LocaleSuggestionProps {
  /** Banner copy for each locale that may be suggested, in that locale. */
  copy: Partial<Record<Locale, LocaleSuggestionCopy>>;
}

/**
 * Offer this page in the visitor's preferred language, when there is one.
 *
 * @param props - see {@link LocaleSuggestionProps}.
 */
export function LocaleSuggestion({ copy }: LocaleSuggestionProps): React.JSX.Element | null {
  const pathname = usePathname();
  const path: PublicPath = isPublicPath(pathname) ? pathname : "/";
  const [locale, setLocale] = useState<Locale | null>(null);

  useEffect(() => {
    if (readLocaleCookie() !== null) return;
    const offered = Object.keys(copy) as Locale[];
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the cookie and navigator.languages exist only after hydration of a static page
    setLocale(suggestedLocale(navigator.languages, offered));
  }, [copy]);

  const text = locale ? copy[locale] : undefined;
  if (!locale || !text) return null;
  return (
    <aside
      lang={locale}
      data-test-id="locale-suggestion"
      className="flex items-center justify-center gap-m bg-neutral-50 px-l py-s text-sm text-neutral-750"
    >
      <span>{text.suggestion}</span>
      <a
        href={localisedPath(path, locale)}
        hrefLang={locale}
        data-test-id="locale-suggestion-accept"
        className="font-medium underline"
        onClick={() => writeLocaleCookie(locale)}
      >
        {text.suggestionAction}
      </a>
      <button
        type="button"
        aria-label={text.suggestionDismiss}
        data-test-id="locale-suggestion-dismiss"
        onClick={() => {
          writeLocaleCookie(DEFAULT_LOCALE);
          setLocale(null);
        }}
      >
        <CloseIcon />
      </button>
    </aside>
  );
}
